import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Layers,
  BookOpen,
  Image as ImageIcon,
  Settings,
  LogOut,
  ExternalLink,
  KeyRound,
} from "lucide-react";
import { useAuth } from "./AuthContext";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/pages", label: "Pagina's", icon: FileText },
  { to: "/admin/blocks", label: "Contentblokken", icon: Layers },
  { to: "/admin/stories", label: "Verhalen", icon: BookOpen },
  { to: "/admin/media", label: "Media", icon: ImageIcon },
  { to: "/admin/settings", label: "Instellingen", icon: Settings },
];

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div
      data-testid="admin-layout"
      className="min-h-screen bg-ink-950 text-white flex"
    >
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className="w-72 shrink-0 bg-ink-900 border-r border-white/[0.06] flex flex-col"
      >
        <div className="px-7 py-7 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <div className="relative w-9 h-9 flex items-center justify-center border border-champagne/50">
              <span className="font-display text-champagne text-lg">V</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display text-lg text-white tracking-tight">
                Maison Vellore
              </span>
              <span className="text-[9px] tracking-[0.3em] uppercase text-champagne mt-0.5">
                CMS Admin
              </span>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-6 px-4 flex flex-col gap-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm font-body transition-colors ${
                    isActive
                      ? "bg-champagne/10 text-champagne border-l-2 border-champagne"
                      : "text-zinc-300 hover:bg-ink-850 border-l-2 border-transparent"
                  }`
                }
              >
                <Icon size={16} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 pb-6 pt-4 border-t border-white/[0.06]">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-view-site"
            className="flex items-center gap-2 px-4 py-3 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-champagne transition-colors"
          >
            <ExternalLink size={13} />
            Bekijk site
          </a>
          <div className="px-4 py-3 mt-1 hairline-t pt-4">
            <p
              data-testid="admin-current-email"
              className="text-[11px] text-zinc-500 truncate font-body"
            >
              {user?.email}
            </p>
            <p className="text-[10px] text-zinc-600 tracking-[0.2em] uppercase mt-1">
              {user?.role}
            </p>
          </div>
          <Link
            to="/admin/account"
            data-testid="admin-account-link"
            className="mt-2 w-full flex items-center gap-2 px-4 py-3 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-champagne transition-colors"
          >
            <KeyRound size={13} />
            Wachtwoord
          </Link>
          <button
            type="button"
            data-testid="admin-logout"
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-4 py-3 text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-champagne transition-colors"
          >
            <LogOut size={13} />
            Uitloggen
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <div className="max-w-[1280px] mx-auto px-8 lg:px-12 py-10 lg:py-14">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
